import { ApolloError, TypedDocumentNode, WatchQueryFetchPolicy, useQuery } from "@apollo/client";
import { Box, Stack, SxProps, Theme, useMediaQuery, useTheme } from "@mui/material";
import {
  GridColDef,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { DataGridProps } from "components/DataGrid";
import { Omnisearch } from "components/Omnisearch/Omnisearch";
import { OmnisearchPillRow } from "components/Omnisearch/OmnisearchPillRow";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  FilterKey,
  OmnisearchGridColDef,
  OmnisearchPersistenceScope,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { useParsedOmnisearch } from "hooks/useParsedOmnisearch";
import { OmnisearchDataProvider, useOmnisearchGridOptional } from "providers/OmnisearchDataContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { FilterGroup } from "./Omnisearch/FilterPopover";
import { OmnisearchColumnPopover } from "./OmnisearchColumnPopover";
import { TierIIYearPicker } from "./TierIIYearPicker";
import { ToolbarFilterButton, ToolbarManageColumnsButton } from "./ToolbarCommandButtons";

type OmnisearchInputRef = React.RefObject<HTMLInputElement | null>;
type OmnisearchResetButtonRef = React.RefObject<HTMLButtonElement | null>;
export type OmnisearchCommandButtonsContext = {
  search: string;
};

/** @deprecated Used by Omnisearch V1 (`OmnisearchDataDisplay`). */
export type DataDisplayProps<TData, TRow extends GridValidRowModel> = {
  loading: boolean;
  previousData: TData | undefined;
  data: TData | undefined;
  error: ApolloError | undefined;
  items: TRow[];
  count: number;
  paginationModel: GridPaginationModel;
  setPaginationModel: (model: GridPaginationModel) => void;
  // Grid state passed from OmnisearchDataDisplay to avoid duplicate hook calls
  columns: GridColDef<TRow>[];
  omnisearch: string;
  sortModel: GridSortModel;
  setOmnisearch: (val: string) => void;
  setOmnisearchImmediate: (val: string) => void;
  setSortModel: (model: GridSortModel) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  setColumnWidthModel: (params: GridColumnResizeParams) => void;
  unsetColumnWidthModel: (field: string) => void;
};

/** @deprecated Use `OmnisearchDataGridV2` from `hooks/omnisearch-v2` instead. */
export type OmnisearchDataDisplayProps<TData, TVariables, TRow extends GridValidRowModel> = {
  /** Component to render the data. */
  dataDisplay: (props: DataDisplayProps<TData, TRow>) => React.ReactNode;
  omnisearchInputRef?: OmnisearchInputRef;
  omnisearchResetButtonRef?: OmnisearchResetButtonRef;
  autoFocus?: boolean;
  /** key to be used as an identifier for persisted state in localStorage */
  persistenceKey: string;
  columns: OmnisearchGridColDef<TRow>[];
  dataQuery: TypedDocumentNode<TData, TVariables>;
  /**
   * Extra query variables merged into the omnisearch query. Must NOT contain
   * `search` or `sort` — those are owned by the omnisearch input and the grid
   * sort model; pinning them here silently disables user search/sorting
   * (ENG-15658). Use `defaultSearch` / `initialSortModel` instead.
   */
  dataQueryVariables?: Omit<TVariables, "search" | "sort">;
  defaultSearch?: string;
  /**
   * Show `defaultSearch` in the filter menu as pre-filled field values. Opt-in
   * because most callers use `defaultSearch` to scope a grid (`tenantId:x`),
   * which isn't a filter the user chose and shouldn't appear as one. Set it where
   * the defaults ARE user-facing filters, so the menu agrees with the toolbar.
   */
  showDefaultsInFilterMenu?: boolean;
  /** @deprecated Use shouldShowManageColumns instead - managed internally now */
  manageColumnsCommandButton?: React.ReactNode;
  /** When true, shows the manage columns button (rendered internally) */
  shouldShowManageColumns?: boolean;
  /**
   * Command buttons to display to the right of the omnisearch input.
   */
  commandButtons?:
    | React.ReactNode[]
    | ((context: OmnisearchCommandButtonsContext) => React.ReactNode[]);
  /**
   * Command buttons to display to the left of the omnisearch input.
   */
  startCommandButtons?:
    | React.ReactNode[]
    | ((context: OmnisearchCommandButtonsContext) => React.ReactNode[]);
  /** sx for the Box wrapping the component */
  sx?: SxProps<Theme>;

  omnisearchWrapperSx?: SxProps<Theme>;
  /** Opt-in styles for the search input field (e.g. a white background). Undefined = unchanged. */
  searchFieldSx?: SxProps<Theme>;
  /** Additional columns to exclude from the Omnisearch filter popover. ("actions" is excluded by default) */
  excludeFilterColumns?: GridColDef["field"][];
  additionalFilterColumns?: FilterKey[];
  initialFilters?: { field: string; value: string | boolean }[];

  /** onClick handler for the button displayed in the grid when there is no data for the default search */
  noDataOnButtonClick?: () => void;
  getItems: (data: TData) => TRow[];
  getCount: (data: TData) => number;
  initialPageSize?: number;
  initialSortModel?: GridSortModel;

  /**
   * Function to handle the datagrid's onLoadingStateChange event.
   */
  onLoadingStateChange?: (loading: boolean) => void;
  onSearchChanged?: (search: string) => void;
  /**
   * Apollo client fetch policy. Defaults to "cache-and-network"
   */
  fetchPolicy?: WatchQueryFetchPolicy;
  /**
   * Indicates if Omnisearch should use the url querystring. Set to false to use
   * an internal search variable (i.e. for a modal).
   */
  isURLDriven?: boolean;
  otherLoading?: boolean;
  showFilters?: boolean;
  /**
   * Render Filters and Manage Columns as their icons alone, sitting beside the
   * other icon commands the page supplies. Default: false, which keeps the
   * labelled buttons every other grid is built with.
   */
  iconToolbarButtons?: boolean;
  hideOmnisearch?: boolean;
  /** when true, provide extra padding around grid */
  withPadding?: boolean;
  /** (optional) skip running query if true. Defaults to false */
  skip?: boolean;
  shouldPoll?: (data: TData) => number | undefined;
  onQueryError?: (error: ApolloError) => void;
  onApolloLoadingStateChange?: (loading: boolean) => void;
  onPreviousDataStateChange?: (previousData: TData | undefined) => void;
  onDataStateChange?: (data: TData | undefined) => void;
  onQueryStateChange?: (state: { data: TData | undefined; loading: boolean }) => void;
  /** Callback that receives the refetch function when the query is ready */
  onRefetchReady?: (refetch: () => void) => void;
  getRowLink?: DataGridProps<TRow>["getRowLink"];
  /** Optional filter groups for organizing filters in the popover */
  filterGroups?: FilterGroup[];
  /** Optional key aliases for omnisearch (e.g., { tags: "facilityTags" }) */
  keyAliases?: Record<string, string>;
  /**
   * Omnisearch keys valid in saved filters but not backed by a column filter —
   * e.g. page-level toggles like the fulfillment "View by" selector. Forwarded
   * to Omnisearch so filterValidKeys doesn't strip them on restore. Pass a
   * stable (module-level) array reference.
   */
  preservedFilterKeys?: string[];
  /** Opt-in for wide (3-column) filter layout. */
  wideLayout?: boolean;
  /** Initial column visibility model - columns hidden by default when user hasn't customized settings */
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  /**
   * Optional tenantId for backend queries. Will default to tenantId from the URL if present. (e.g. for customer routes)
   * Staff routes will not have the tenantId in the URL
   * - Do not provide tenantId if you intentionally want unscoped filters, such as on the fulfillment grid
   * - Provide the tenantId when you are operating with records specific to a tenant, such as with payments or mail.
   */
  tenantId?: string;
  /** Optional key for the year picker. */
  yearPickerKey?: string;
  /** Optional default value for the year picker. */
  yearPickerDefaultValue?: string;
  /** Optional boolean to show the "All" option in the year picker. Selecting All will remove the year filter. */
  yearPickerShowAllOption?: boolean;
  /** Optional handler for when the year picker value changes. */
  onYearPickerChange?: (year: string | undefined) => void;
  /** Debounce delay in ms for typing. Defaults to 500ms. */
  debounceMs?: number;
  /** When false, the omnisearch filter string is not persisted to localStorage
   *  and any previously-persisted value is ignored on mount. Useful for modal
   *  pickers that should open with a clean slate on every open. Defaults to
   *  the grid-level default (no persistence unless the grid opts in). */
  persistOmnisearch?: boolean;
  /** Scope for the persisted omnisearch value. "tenant" (default) keys storage
   *  by the current tenant and disables persistence when none is resolvable;
   *  use "global" for grids without tenant context (staff-wide dashboards). */
  persistOmnisearchScope?: OmnisearchPersistenceScope;
  /** When false, the sort model resets to `initialSortModel` on each mount
   *  instead of being persisted. Defaults to true. */
  persistSortModel?: boolean;
  /** When true, the outer wrapper fills its parent's height and the data-display
   *  region flex-grows to absorb leftover space. Required for the MUI DataGrid
   *  inside to virtualize rows — otherwise the grid expands to fit all rows
   *  and virtualization is effectively disabled. */
  fillParentHeight?: boolean;
};

/** This component displays omnisearch bar and controls, fetches data,
 *  and passes the data to the `dataDisplay` component provided in the props.
 *
 * @deprecated Use `OmnisearchDataGridV2` from `hooks/omnisearch-v2` instead. */
export function OmnisearchDataDisplay<TData, TVariables, TRow extends GridValidRowModel>({
  omnisearchInputRef,
  omnisearchResetButtonRef,
  autoFocus,
  persistenceKey,
  columns: columnsInput,
  dataQuery,
  dataQueryVariables,
  defaultSearch = "",
  showDefaultsInFilterMenu = false,
  commandButtons,
  startCommandButtons,
  manageColumnsCommandButton,
  shouldShowManageColumns = false,
  sx,
  omnisearchWrapperSx,
  excludeFilterColumns,
  additionalFilterColumns,
  initialFilters,
  getItems,
  getCount,
  onLoadingStateChange,
  onSearchChanged,
  fetchPolicy = "cache-and-network",
  initialPageSize,
  initialSortModel,
  isURLDriven = false,
  otherLoading = false,
  showFilters = true,
  iconToolbarButtons,
  hideOmnisearch = false,
  searchFieldSx,
  withPadding = true,
  skip = false,
  shouldPoll,
  onQueryError,
  onApolloLoadingStateChange,
  onPreviousDataStateChange,
  onDataStateChange,
  onQueryStateChange,
  onRefetchReady,
  filterGroups,
  keyAliases,
  preservedFilterKeys,
  wideLayout,
  initialColumnVisibilityModel,
  tenantId,
  yearPickerKey,
  yearPickerDefaultValue,
  yearPickerShowAllOption,
  onYearPickerChange,
  debounceMs,
  persistOmnisearch,
  persistOmnisearchScope,
  persistSortModel,
  fillParentHeight,
  dataDisplay,
}: OmnisearchDataDisplayProps<TData, TVariables, TRow>) {
  const theme = useTheme();
  const { user } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { tenantId: tenantIdFromParams } = useParams<{ tenantId: string }>();
  const selectedTenantId = tenantId ?? tenantIdFromParams;

  // Check if we're inside an OmnisearchProvider (context with gridState)
  // If so, use context values instead of creating duplicate state
  const existingContext = useOmnisearchGridOptional();

  // Only call useOmnisearchDatagrid if we don't have existing context
  // This prevents duplicate state when wrapped by OmnisearchProvider
  const internalState = useOmnisearchDatagrid({
    persistenceKey,
    columns: columnsInput,
    additionalFilters: additionalFilterColumns,
    excludeFilters: excludeFilterColumns,
    isURLDriven,
    initialPageSize,
    initialSortModel,
    initialFilters,
    isMobile,
    initialColumnVisibilityModel,
    debounceMs,
    persistOmnisearch,
    persistOmnisearchScope,
    persistSortModel,
  });

  // Use context values if available, otherwise use internal state
  const {
    omnisearch,
    setOmnisearch,
    setOmnisearchImmediate,
    getOmnisearchValue,
    createUrlFromOmnisearchKeyValues,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  } = existingContext ?? internalState;

  // Grid state comes from context or internal state
  const {
    paginationModel,
    sortModel,
    setPaginationModel,
    setSortModel,
    columnFilterKeys,
    columns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
  } = existingContext?.gridState ?? internalState;

  // When wrapped in OmnisearchProvider, prop columns may be empty while actual
  // columns come from context. Re-evaluate using resolved columns so the button
  // appears when there are enough real columns.
  const effectiveShouldShowManageColumns = useMemo(() => {
    if (shouldShowManageColumns) return true;
    if (columnsInput.length === 0 && columns.length > 0) {
      return columns.filter((c) => c.type !== "actions").length >= 5;
    }
    return shouldShowManageColumns;
  }, [shouldShowManageColumns, columnsInput.length, columns]);

  // For search string building, use omnisearch (debounced state is internal to hook)
  const debouncedOmnisearch = existingContext ? omnisearch : internalState.debouncedOmnisearch;

  // Track previous page size to detect changes without causing re-renders
  const previousPageSizeRef = useRef(paginationModel.pageSize);

  // Reset to page 0 when page size changes
  useEffect(() => {
    if (paginationModel.pageSize !== previousPageSizeRef.current) {
      previousPageSizeRef.current = paginationModel.pageSize;
      // Only reset if we're not already on page 0
      if (paginationModel.page > 0) {
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      }
    }
  }, [paginationModel.pageSize, paginationModel.page, setPaginationModel]);

  // Manage columns popover state (only used when shouldShowManageColumns is true)
  const [columnListAnchorEl, setColumnListAnchorEl] = useState<HTMLButtonElement | null>(null);
  const showColumnList = Boolean(columnListAnchorEl);

  const handleColumnList = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnListAnchorEl(event.currentTarget);
  }, []);

  const handleCloseColumnList = useCallback(() => {
    setColumnListAnchorEl(null);
  }, []);

  const resetColumnWidth = useCallback(
    (field: string) => {
      const colDef = columnsInput.find((c) => c.field === field);
      if (!colDef) return;
      unsetColumnWidthModel(colDef.field);
    },
    [columnsInput, unsetColumnWidthModel]
  );

  const handleToggleColumn = useCallback(
    (field: string) => {
      const newModel = {
        ...columnVisibilityModel,
        [field]: field in columnVisibilityModel ? !columnVisibilityModel[field] : false,
      };
      if (!newModel[field]) resetColumnWidth(field);
      setColumnVisibilityModel(newModel);
    },
    [setColumnVisibilityModel, resetColumnWidth, columnVisibilityModel]
  );

  // Memoized parsing of omnisearch - avoids redundant parsing across renders
  const parsedOmnisearch = useParsedOmnisearch(omnisearch, keyAliases);

  // Smart merge: removes keys from defaultSearch that exist in omnisearch
  // This prevents duplicate keys like "submissionStatus:A submissionStatus:B"
  const getSearchString = useCallback((defaultSearch: string, omnisearch: string) => {
    if (!omnisearch.trim()) {
      return defaultSearch.trim();
    }
    if (!defaultSearch.trim()) {
      return omnisearch.trim();
    }

    // Extract keys from omnisearch
    const omnisearchKeys = new Set<string>();
    const keyRegex = /([^:\s]+):/g;
    let match;
    while ((match = keyRegex.exec(omnisearch)) !== null) {
      omnisearchKeys.add(match[1]);
    }

    // Parse defaultSearch tokens and filter out ones with keys in omnisearch
    const tokens: string[] = [];
    let current = "";
    let inQuotes = false;
    let quoteChar = "";
    for (let i = 0; i < defaultSearch.length; i++) {
      const char = defaultSearch[i];
      if (!inQuotes && (char === '"' || char === "'")) {
        inQuotes = true;
        quoteChar = char;
        current += char;
      } else if (inQuotes && char === quoteChar) {
        inQuotes = false;
        quoteChar = "";
        current += char;
      } else if (!inQuotes && char === " ") {
        if (current.trim()) {
          tokens.push(current.trim());
          current = "";
        }
      } else {
        current += char;
      }
    }
    if (current.trim()) tokens.push(current.trim());

    // Filter out tokens whose keys exist in omnisearch
    const filteredDefault = tokens
      .filter((token) => {
        const colonIndex = token.indexOf(":");
        if (colonIndex > 0) {
          const key = token.substring(0, colonIndex);
          return !omnisearchKeys.has(key);
        }
        return true;
      })
      .join(" ");

    return [filteredDefault, omnisearch].filter(Boolean).join(" ").trim();
  }, []);

  // Compute search synchronously during render instead of via useEffect→setState.
  // Compute search synchronously during render. The debounce in useOmnisearchInput
  // already prevents query spam during typing — no additional deferral needed.
  const search = useMemo(
    () => getSearchString(defaultSearch, debouncedOmnisearch),
    [defaultSearch, debouncedOmnisearch, getSearchString]
  );

  useEffect(() => onSearchChanged && onSearchChanged(search), [search, onSearchChanged]);

  // Track query start time for metricsLogger duration measurement
  const queryStartRef = useRef<number>(0);
  queryStartRef.current = performance.now();

  const {
    data,
    error,
    loading: dataLoading,
    previousData,
    startPolling,
    stopPolling,
    refetch,
  } = useQuery(dataQuery, {
    variables: {
      search,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: sortModel,
      ...dataQueryVariables,
    },
    fetchPolicy,
    skip,
  });

  // Handle query errors via useEffect (onError is deprecated in Apollo Client 3.14+)
  useEffect(() => {
    if (error && onQueryError) {
      onQueryError(error);
    }
  }, [error, onQueryError]);

  useEffect(() => {
    if (onApolloLoadingStateChange) onApolloLoadingStateChange(dataLoading);
  }, [dataLoading, onApolloLoadingStateChange]);

  useEffect(() => {
    if (onPreviousDataStateChange) onPreviousDataStateChange(previousData);
  }, [previousData, onPreviousDataStateChange]);

  useEffect(() => {
    if (onDataStateChange) onDataStateChange(data);
  }, [data, onDataStateChange]);

  useEffect(() => {
    if (onQueryStateChange) onQueryStateChange({ data, loading: dataLoading });
  }, [data, dataLoading, onQueryStateChange]);

  useEffect(() => {
    if (onRefetchReady) onRefetchReady(() => refetch());
  }, [onRefetchReady, refetch]);

  // Guard against callers pinning search/sort in dataQueryVariables — they're
  // spread after the user-driven values and would silently disable the search
  // bar and column sorting (ENG-15658). The Omit<> on the prop type catches
  // inline literals; this catches memoized/non-literal objects.
  useEffect(() => {
    if (!import.meta.env.DEV || !dataQueryVariables) return;
    const offending = ["search", "sort"].filter((key) => key in dataQueryVariables);
    if (offending.length > 0) {
      console.warn(
        `OmnisearchDataDisplay("${persistenceKey}"): dataQueryVariables contains ${offending.join(
          ", "
        )}, which overrides the user's omnisearch/sort input. Use defaultSearch / initialSortModel instead.`
      );
    }
  }, [dataQueryVariables, persistenceKey]);

  useEffect(() => {
    const pollInterval = data ? shouldPoll?.(data) : undefined;
    if (pollInterval) {
      startPolling(pollInterval);
    } else {
      stopPolling();
    }
  }, [startPolling, stopPolling, shouldPoll, data]);

  const [items, count] = useMemo(() => {
    if (error) return [[], 0];
    if (data) return [getItems(data), getCount(data)];
    if (dataLoading && previousData) return [getItems(previousData), getCount(previousData)];
    return [[], 0];
  }, [data, previousData, dataLoading, error, getItems, getCount]);

  const loading = dataLoading || otherLoading;

  useEffect(() => {
    if (onLoadingStateChange) onLoadingStateChange(loading);
  }, [loading, onLoadingStateChange]);

  const onApplyFilters = useCallback(
    (newOmnisearch: string) => {
      // Use immediate for programmatic filter applies (no debounce needed)
      // setOmnisearchImmediate updates debouncedOmnisearch immediately, and
      // search is computed synchronously via useMemo on the next render.
      setOmnisearchImmediate(newOmnisearch);
    },
    [setOmnisearchImmediate]
  );

  // Render function for the external filter button (passed to Omnisearch)
  // Using a render prop keeps filter state colocated inside Omnisearch
  const renderFilterButton = useCallback(
    (onClick: (event: React.MouseEvent<HTMLButtonElement>) => void) => (
      <ToolbarFilterButton iconOnly={iconToolbarButtons} onClick={onClick} />
    ),
    [iconToolbarButtons]
  );

  const yearPickerValue = useMemo<string | undefined>(() => {
    if (yearPickerKey) {
      return (
        (parsedOmnisearch.getValue(yearPickerKey) as string | undefined) ?? yearPickerDefaultValue
      );
    }
    return undefined;
  }, [yearPickerKey, yearPickerDefaultValue, parsedOmnisearch]);

  const onYearPickerValueChanged = useCallback(
    (value: string | undefined) => {
      if (yearPickerKey) {
        // Edit only the year's own term. Rebuilding the query from its parsed
        // filters instead would drop the free-text search, which is not a
        // filter and so has no place in that representation.
        const outputKey = keyAliases?.[yearPickerKey] ?? yearPickerKey;
        // A grid that renames the year key can still be arrived at with the
        // plain one in the URL; leaving that term would apply two years at once.
        if (outputKey !== yearPickerKey) {
          removeOmnisearchKeyValue(yearPickerKey);
        }
        if (value) {
          editOmnisearchKeyValues([[outputKey, value]]);
        } else {
          removeOmnisearchKeyValue(outputKey);
        }
      }
      if (onYearPickerChange) onYearPickerChange(value);
    },
    [
      yearPickerKey,
      keyAliases,
      editOmnisearchKeyValues,
      removeOmnisearchKeyValue,
      onYearPickerChange,
    ]
  );

  const renderedCommandButtons = useMemo(() => {
    if (!commandButtons) return [];

    if (Array.isArray(commandButtons)) {
      return commandButtons;
    }

    if (typeof commandButtons === "function") {
      return commandButtons({
        search,
      });
    }

    return [];
  }, [commandButtons, search]);

  const renderedStartCommandButtons = useMemo(() => {
    if (!startCommandButtons) return [];

    if (Array.isArray(startCommandButtons)) {
      return startCommandButtons;
    }

    if (typeof startCommandButtons === "function") {
      return startCommandButtons({
        search,
      });
    }

    return [];
  }, [startCommandButtons, search]);

  if (!user) return <></>;

  // Content to render (same whether wrapped in provider or not)
  const content = (
    <Box
      sx={{
        px: withPadding ? 3 : 0,
        ...(fillParentHeight && {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }),
      }}
    >
      <Stack
        sx={{
          marginBottom: !hideOmnisearch ? theme.spacing(3) : theme.spacing(2),
          gap: theme.spacing(1.5),
          ...(fillParentHeight && { flex: "0 0 auto" }),
          ...omnisearchWrapperSx,
        }}
        direction="row"
        flexWrap="wrap"
        alignItems="center"
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: {
              xs: "100%",
              lg: "inherit",
            },
            maxWidth: "1022px",
            flexGrow: 1,
          }}
          gap={theme.spacing(1.5)}
        >
          {renderedStartCommandButtons.length > 0 && renderedStartCommandButtons}
          {!hideOmnisearch && (
            <Omnisearch
              inputRef={omnisearchInputRef}
              resetRef={omnisearchResetButtonRef}
              autoFocus={autoFocus}
              omnisearch={omnisearch}
              setOmnisearch={setOmnisearch}
              setOmnisearchImmediate={setOmnisearchImmediate}
              onApplyFilters={onApplyFilters}
              columnFilterKeys={columnFilterKeys}
              filterGroups={filterGroups}
              wideLayout={wideLayout}
              showFilterButtonInside={false}
              showExternalFilterButton={showFilters}
              renderFilterButton={renderFilterButton}
              persistenceKey={persistenceKey}
              keyAliases={keyAliases}
              defaultSearch={showDefaultsInFilterMenu ? defaultSearch : undefined}
              preservedFilterKeys={preservedFilterKeys}
              tenantId={selectedTenantId}
              sx={{ flexGrow: 1 }}
              searchFieldSx={searchFieldSx}
            />
          )}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gap: theme.spacing(1.5),
            flexWrap: "wrap",
            // Below the pill row once the toolbar wraps, so the pills stay
            // visually attached to the search box they belong to. On one line
            // (lg and up) the pills already wrap underneath everything.
            order: { xs: 1, lg: 0 },
          }}
        >
          {yearPickerKey && (
            <TierIIYearPicker
              persistQueryParam={false}
              showAllOption={yearPickerShowAllOption}
              value={yearPickerValue}
              onChange={(value) => onYearPickerValueChanged(value ? String(value) : undefined)}
            />
          )}

          {/* Support both legacy manageColumnsCommandButton and new shouldShowManageColumns */}
          {manageColumnsCommandButton}
          {effectiveShouldShowManageColumns && !manageColumnsCommandButton && (
            <>
              <ToolbarManageColumnsButton
                iconOnly={iconToolbarButtons}
                onClick={handleColumnList}
              />
              {showColumnList && (
                <OmnisearchColumnPopover
                  showColumnList={showColumnList}
                  anchorEl={columnListAnchorEl}
                  handleCloseColumnList={handleCloseColumnList}
                  columns={columns.filter(
                    (column) => column.headerName !== "actions" && column.field !== "actions"
                  )}
                  handleToggleColumn={handleToggleColumn}
                  selectedColumns={columnVisibilityModel}
                  setSelectedColumns={(model) => {
                    setColumnVisibilityModel(model);
                  }}
                />
              )}
            </>
          )}
          {renderedCommandButtons.length > 0 && renderedCommandButtons}
        </Stack>

        {/* Applied-filter pills, on their own line below the toolbar. Renders
            nothing unless the preview is on and something is filtered. */}
        {!hideOmnisearch && (
          <OmnisearchPillRow
            omnisearch={omnisearch}
            onChange={setOmnisearchImmediate ?? setOmnisearch}
            onApply={onApplyFilters ?? setOmnisearch}
            columnFilterKeys={columnFilterKeys}
            keyAliases={keyAliases}
            preservedFilterKeys={preservedFilterKeys}
            tenantId={selectedTenantId}
          />
        )}
      </Stack>
      <Box position="relative" sx={{ ...(fillParentHeight && { flex: 1, minHeight: 0 }), ...sx }}>
        {dataDisplay({
          data,
          items,
          count,
          loading,
          previousData,
          error,
          paginationModel,
          setPaginationModel,
          // Grid state - passed to avoid duplicate hook calls in HydratedOmnisearchDataGrid
          columns,
          omnisearch,
          sortModel,
          setOmnisearch,
          setOmnisearchImmediate,
          setSortModel,
          columnVisibilityModel,
          setColumnVisibilityModel,
          setColumnWidthModel,
          unsetColumnWidthModel,
        })}
      </Box>
    </Box>
  );

  // If we already have context from OmnisearchProvider, don't wrap again
  if (existingContext) {
    return content;
  }

  // No existing context - wrap in provider for backwards compatibility
  return (
    <OmnisearchDataProvider
      omnisearch={omnisearch}
      setOmnisearch={setOmnisearch}
      setOmnisearchImmediate={setOmnisearchImmediate}
      editOmnisearchKeyValues={editOmnisearchKeyValues}
      removeOmnisearchKeyValue={removeOmnisearchKeyValue}
      getOmnisearchValue={getOmnisearchValue}
      createUrlFromOmnisearchKeyValues={createUrlFromOmnisearchKeyValues}
      keyAliases={keyAliases}
    >
      {content}
    </OmnisearchDataProvider>
  );
}
